import { VANILLA } from '@approot/constants';
import {
  BidAskByDateType,
  IndicativePremium,
  QuoteSide,
  RFQ_QUOTE_DEFAULT,
  RFQ_QUOTE_INDICATIVE,
  RfqBidOfferQuotes,
  RfqQuote,
  RfqTab
} from '@approot/models';
import { TabItem } from '@approot/provider-quote';
import {
  ApiPriceIsFrom,
  ClientEventQuoteExtended,
  ClientEventQuoteExtendedV2,
  DcdMarginForOptionPrice,
  HedgeQuote,
  IndicativeQuotes,
  PortfolioResult,
  PortfolioResultV2,
  RFQDetailsDTO
} from '@approot/services';
import {
  getBidOfferLabel,
  getReferenceCounterNotional,
  getReferenceNotional,
  getStrategy,
  getTarfPremiumAsPercent,
  handleDefaultPremiumInStyle,
  premiumInPercent,
  setDisplayVols
} from '@approot/services/rfq/rfq-tab/rfq-tab';
import { clone, reformatDate } from '@approot/utilities';
import { LegComponent } from '@approot/widgets';
import { sortBy } from 'lodash';

export function marginColor(
  value: number,
  apiPriceIsFrom: ApiPriceIsFrom
): string {
  if (value < 0 && apiPriceIsFrom === DcdMarginForOptionPrice) {
    return 'negative';
  }
  return null;
}

export function createQuote(
  rfqShortName: string,
  premiumValueToUse: number,
  eventQuote: ClientEventQuoteExtended | ClientEventQuoteExtendedV2,
  portfolioResult: PortfolioResult | PortfolioResultV2,
  side: QuoteSide,
  legPremiums: number[],
  legCashPremiums: number[],
  cashToPremiumStyleDividingAmounts: number[],
  legsInverted: boolean[],
  tabData: RfqTab | TabItem,
  rfqDetailsDTO: RFQDetailsDTO,
  allFaceCurrenciesTheSame: boolean,
  selectedLegs: LegComponent[],
  forwardPointsMultiplier: number,
  quoteReference: string,
  hedgeQuotes: HedgeQuote[],
  executionType: 'Live' | 'DX',
  executionContextType: string,
  indicative = false
): RfqQuote {
  // if there is a premiumValue and an rfe has not been sent then we can build a quote
  const bidAskAmount =
    rfqShortName === 'dcd' ? premiumValueToUse : Math.abs(premiumValueToUse);

  const bidAsk = {
    type: null,
    amount: bidAskAmount,
    currency: eventQuote.premiumCurrency,
    marginSpread: eventQuote.marginSpread,
    color: marginColor(premiumValueToUse, eventQuote.apiPriceIsFrom)
  };

  let bidAsksByDate: BidAskByDateType[] = null;

  if (tabData.isVolBased && portfolioResult.premiumBySettlementDate) {
    const items = sortBy(
      Object.entries(portfolioResult.premiumBySettlementDate),
      item => item[0]
    );
    if (items.length > 0) {
      bidAsksByDate = items.map(([date, value]) => {
        const premiumValue =
          side === 'bid'
            ? value.bid[eventQuote.premiumCurrency]
            : value.ask[eventQuote.premiumCurrency];

        return {
          date: reformatDate(date, 'D MMM YYYY'),
          amount: Math.abs(premiumValue),
          type: getBidOfferLabel(side, premiumValue),
          currency: eventQuote.premiumCurrency
        };
      });
    }
  }

  if (tabData.quoteOptions.showPayRecieve) {
    bidAsk.type = getBidOfferLabel(side, premiumValueToUse);
  }

  const hedge = {
    type: '',
    amount: null,
    currency: '',
    value: null
  };

  if (
    !indicative &&
    rfqDetailsDTO.hedge !== 'None' &&
    ((side === 'offer' &&
      eventQuote.hedgeOffer !== undefined &&
      eventQuote.hedgeRateOffer !== undefined) ||
      (side === 'bid' &&
        eventQuote.hedgeBid !== undefined &&
        eventQuote.hedgeRateBid !== undefined))
  ) {
    hedge.type =
      eventQuote[side === 'offer' ? 'hedgeOffer' : 'hedgeBid'] > 0
        ? 'Sell'
        : 'Buy';

    hedge.amount = Math.abs(
      side === 'offer' ? eventQuote.hedgeOffer : eventQuote.hedgeBid
    );
    hedge.currency = rfqDetailsDTO.quotePair.base;
    hedge.value =
      side === 'offer' ? eventQuote.hedgeRateOffer : eventQuote.hedgeRateBid;
  }

  const quotedPremiumSection = {
    quotedPremium: null,
    showPercent: false,
    currency: ''
  };

  if (
    tabData.quoteOptions.showQuotedPremiumSection &&
    allFaceCurrenciesTheSame
  ) {
    // if all faceCurrencies are the same
    const strategy = getStrategy(tabData.title.productName.shortName);
    switch (strategy) {
      case 'TargetProfitForward':
        quotedPremiumSection.showPercent = true;
        quotedPremiumSection.currency = eventQuote.premiumCurrency;
        quotedPremiumSection.quotedPremium = getTarfPremiumAsPercent(
          eventQuote,
          side,
          rfqDetailsDTO.legs,
          tabData.title.quotePair
        );
        break;
      default:
        if (
          strategy !== 'Custom' ||
          selectedLegs.every(
            legSelected => legSelected.leg.productType === VANILLA
          )
        ) {
          handleDefaultPremiumInStyle(
            quotedPremiumSection,
            eventQuote,
            side,
            rfqDetailsDTO,
            forwardPointsMultiplier
          );
        }
        break;
    }
  }

  const spotPremium = portfolioResult?.spotPremium;
  const forwardPremium = portfolioResult?.forwardPremium;
  const legPremium = portfolioResult?.legPremium;
  const leg0 = selectedLegs[0].form.value;
  const notional = getReferenceNotional(
    selectedLegs[0].leg.productType,
    rfqDetailsDTO?.legs[0].eventSchedule,
    tabData.title.quotePair.base,
    tabData.title.quotePair.term,
    {
      currency: leg0.currency,
      notional: leg0.notional,
      notional1: leg0.notional1
    }
  );

  const counterNotional = getReferenceCounterNotional(
    selectedLegs[0].leg.productType,
    rfqDetailsDTO?.legs[0].eventSchedule,
    tabData.title.quotePair.base,
    tabData.title.quotePair.term,
    {
      currency: leg0.currency,
      counterNotional: leg0.counterNotional,
      counterNotional1: leg0.counterNotional1
    }
  );

  let quote: RfqQuote = {
    type: side,
    bank: eventQuote.provider,
    providerId: eventQuote.providerId,
    id: eventQuote.id,
    hedge,
    bidAsk,
    bidAsksByDate,
    quotedPremiumSection,
    vol: null,
    legPremiums,
    legCashPremiums,
    cashToPremiumStyleDividingAmounts,
    legsInverted,
    quoteId: side === 'bid' ? eventQuote.bidQuoteId : eventQuote.offerQuoteId,
    status: indicative ? RFQ_QUOTE_INDICATIVE : RFQ_QUOTE_DEFAULT,
    timestamp: eventQuote.timestamp,
    timeLimit: eventQuote.timelimit,
    description: eventQuote.description,
    spotPremium: side === 'bid' ? spotPremium?.bid : spotPremium?.ask,
    forwardPremium: side === 'bid' ? forwardPremium?.bid : forwardPremium?.ask,
    legPremium: side === 'bid' ? legPremium?.bid : legPremium?.ask,
    premiumStyle: eventQuote.premiumStyle,
    indicativePremium: indicativePremium(eventQuote),
    apiPriceIsFrom: eventQuote.apiPriceIsFrom,
    premiumCurrency: eventQuote.premiumCurrency,
    pips:
      ((side === 'bid'
        ? eventQuote.premiumAmountBid
        : eventQuote.premiumAmountOffer) /
        notional) *
      forwardPointsMultiplier,
    premiumInPercent: premiumInPercent(
      eventQuote.premiumCurrency,
      tabData.title.quotePair.base,
      tabData.title.quotePair.term,
      side === 'bid'
        ? eventQuote.premiumAmountBid
        : eventQuote.premiumAmountOffer,
      notional,
      counterNotional
    ),
    quoteReference,
    hedgeQuotes,
    executionType,
    executionContextType
  };

  quote = setDisplayVols(
    quote,
    eventQuote,
    tabData.legs,
    rfqShortName,
    side,
    tabData.isVolBased
  );

  return quote;
}

export function mungeQuotes(quotesIn: RfqBidOfferQuotes): RfqBidOfferQuotes {
  const quotes = clone(quotesIn);
  quotes.bids.forEach(bid => maybeInvert(bid));
  quotes.offers.forEach(offer => maybeInvert(offer));
  return quotes;
}

export function mungeIndicativeQuotes(
  quotesIn: IndicativeQuotes
): IndicativeQuotes {
  maybeInvert(quotesIn.bid.quote as RfqQuote);
  maybeInvert(quotesIn.offer.quote as RfqQuote);
  return quotesIn;
}

function maybeInvert(side: RfqQuote) {
  if (!side?.legsInverted) {
    return;
  }
  side.vol?.left.forEach((_, i) => {
    if (!side.legsInverted[i]) {
      return;
    }
    let [a, b] = [side.vol.left[i].value, side.vol.right[i].value];

    side.vol.left[i].value = b;
    side.vol.right[i].value = a;
  });
}

function indicativePremium(
  eventQuote: ClientEventQuoteExtended | ClientEventQuoteExtendedV2
): IndicativePremium {
  return {
    premiumBidPipsTerm: eventQuote.premiumBidPipsTerm_,
    premiumBidPercentBase: eventQuote.premiumBidPercentBase_,
    premiumBidPercentTerm: eventQuote.premiumBidPercentTerm_,
    premiumOfferPercentBase: eventQuote.premiumOfferPercentBase_,
    premiumOfferPercentTerm: eventQuote.premiumOfferPercentTerm_,
    premiumOfferPipsTerm: eventQuote.premiumOfferPipsTerm_
  };
}
